import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Eye, Clock } from "lucide-react";
import type { RecentFeedItem } from "@/services/api/gallery";
import { getThumbnailUrl } from "@/features/categories/utils/fileUtils";
import { generateCategorySlug } from "@/features/categories/utils/categoryUtils";
import { FileIcon } from "@/features/categories/components/shared/FileIcon";
import { Badge } from "@/shared/ui/badge";
import { DEFAULT_TAG_COLOR } from "@/services/api/tags";
import { getFileType, formatRelative } from "../utils/feedUtils";

export function FeedCard({ entry }: { entry: RecentFeedItem }) {
  const [imageError, setImageError] = useState(false);
  const { file, categoryName, categoryDisplayName } = entry;
  const thumbnailUrl = file.largeThumbnail?.url || file.smallThumbnail?.url;
  const fullUrl = thumbnailUrl ? getThumbnailUrl(thumbnailUrl) : undefined;
  const fileType = getFileType(file.extension, file.contentType);
  const displayCategory = categoryDisplayName || categoryName;

  return (
    <Link
      to="/file-explorer/$categoryName"
      params={{ categoryName: generateCategorySlug(categoryName) }}
      search={{ fileId: file.fileId }}
      className="group flex gap-4 rounded-xl border border-grid-200 bg-white p-3 shadow-sm shadow-ink-900/5 transition-all hover:border-imperial-300 hover:shadow-md"
    >
      <div className="flex h-44 w-64 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-grid-50">
        {fullUrl && !imageError ? (
          <img
            src={fullUrl}
            alt={file.name}
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : (
          <FileIcon type={fileType} className="h-8 w-8 text-ink-300" />
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
        <div className="flex items-center gap-2">
          <Badge
            variant="secondary"
            className="shrink-0 text-[10px] px-1.5 py-0"
          >
            {displayCategory}
          </Badge>
          <span className="flex items-center gap-1 text-[11px] text-ink-400">
            <Clock className="h-3 w-3" />
            {formatRelative(file.createdDate)}
          </span>
        </div>

        <p className="line-clamp-1 text-sm font-medium text-ink-900 group-hover:text-imperial-600">
          {file.name}
          {file.extension}
        </p>

        <div className="flex items-center gap-3">
          {file.viewCount > 0 && (
            <span className="flex items-center gap-1 text-[11px] text-ink-400">
              <Eye className="h-3 w-3" />
              {file.viewCount} {file.viewCount === 1 ? "view" : "views"}
            </span>
          )}
          {file.tags.length > 0 && (
            <div className="flex items-center gap-1">
              {file.tags.slice(0, 3).map((tag) => {
                const tagColor = tag.color || DEFAULT_TAG_COLOR;
                const isDefault = tagColor === DEFAULT_TAG_COLOR;
                return (
                  <Badge
                    key={tag.id}
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 font-medium"
                    style={{
                      backgroundColor: `${tagColor}20`,
                      color: isDefault ? "#6B7280" : tagColor,
                      borderColor: `${tagColor}40`,
                    }}
                  >
                    {tag.name}
                  </Badge>
                );
              })}
              {file.tags.length > 3 && (
                <span className="text-[10px] text-ink-400">
                  +{file.tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
